import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * 새 배포 알림 — DYOPS `shared/ui/UpdatePrompt` 이식(2026-09-28 way 「업데이트가 되면 업데이트 알람」).
 *
 * 왜: 해시 파일이라 `index.html` 을 다시 받지 않으면 옛 화면이 계속 돈다. 회계·배차는 창을 하루 종일 열어 둔다
 *   (같은 날 개선요청 배지가 아침 숫자에 멈춰 있던 것도 같은 부류).
 * 🔴 저절로 새로고침하지 않는다 — 입력 중인 폼이 날아간다. 알림만 띄우고 새로고침은 누를 때만.
 *   - 빌드가 `__APP_VERSION__`(번들)과 `dist/version.json` 에 같은 값을 심는다(`vite.config.ts`).
 *   - 창으로 돌아올 때 + 5분마다(최소 60초 간격) `/version.json` 을 캐시 없이 읽는다. 다르면 아래 배너.
 *   - version.json 이 없거나 읽기 실패면 조용히 넘어간다(로컬 dev·옛 배포).
 * 🔴 설치형 앱(홈 화면)은 새로 열기보다 「돌아오기」가 대부분이다(2026-10-22 way 「앱으로 설치했을 때 업데이트 팝업이 잘 안 나온다」).
 *   - 폰이 잠에서 깬 직후는 망이 아직 안 붙어 첫 읽기가 실패한다 → 실패엔 60초 막음을 걸지 않고 5·15·30초 뒤 다시 읽는다.
 *     (예전엔 실패해도 막음이 걸려, 몇 분 쓰고 닫는 기사앱은 다음 5분 주기 전에 끝나 배너를 못 봤다.)
 *   - 복귀 신호를 visibilitychange 하나에 기대지 않는다 — focus·pageshow·online 도 본다.
 * 담당자 웹·기사앱 둘 다 뜬다(`App.tsx` 최상단).
 */

const THROTTLE_MS = 60_000;
const POLL_MS = 5 * 60_000;
const RETRY_MS = [5_000, 15_000, 30_000];
// 렌더 점검(node)처럼 define 이 없는 환경에서도 죽지 않게.
const CURRENT = typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : null;

async function deployedVersion(): Promise<string | null> {
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const v = ((await res.json()) as { version?: unknown } | null)?.version;
    return typeof v === 'string' && v.length > 0 ? v : null;
  } catch {
    return null; // 네트워크 실패·SPA rewrite 로 index.html 이 온 경우
  }
}

export default function UpdatePrompt() {
  const [available, setAvailable] = useState(false);
  const [dismissed, setDismissed] = useState<string | null>(null);
  const [latest, setLatest] = useState<string | null>(null);
  const last = useRef(0);
  const retry = useRef<number | undefined>(undefined);

  const check = useCallback(async (attempt = 0) => {
    if (!CURRENT) return;
    if (attempt === 0 && Date.now() - last.current < THROTTLE_MS) return;
    window.clearTimeout(retry.current);
    const v = await deployedVersion();
    if (v === null) {
      // 깬 직후 망이 안 붙은 실패 — 막음을 걸지 않고, 화면이 보이는 동안만 조금 뒤 다시.
      const wait = RETRY_MS[attempt];
      if (wait !== undefined && document.visibilityState === 'visible') {
        retry.current = window.setTimeout(() => void check(attempt + 1), wait);
      }
      return;
    }
    last.current = Date.now();
    if (v !== CURRENT) { setLatest(v); setAvailable(true); }
  }, []);

  useEffect(() => {
    const onBack = () => { if (document.visibilityState === 'visible') void check(); };
    const winEvents = ['focus', 'pageshow', 'online'] as const;
    document.addEventListener('visibilitychange', onBack);
    for (const e of winEvents) window.addEventListener(e, onBack);
    const timer = window.setInterval(() => void check(), POLL_MS);
    void check();
    return () => {
      document.removeEventListener('visibilitychange', onBack);
      for (const e of winEvents) window.removeEventListener(e, onBack);
      window.clearInterval(timer);
      window.clearTimeout(retry.current);
    };
  }, [check]);

  // 「나중에」로 닫아도 그 뒤 또 새 배포가 나오면 다시 띄운다.
  if (!available || (latest !== null && dismissed === latest)) return null;
  return (
    <div role="region" aria-label="새 버전 안내"
         className="pointer-events-none fixed inset-x-0 bottom-[calc(20px+max(var(--bottom-bar),env(safe-area-inset-bottom)))] z-[60] flex justify-center px-3">
      <div className="pointer-events-auto flex w-full max-w-xl items-center gap-3 rounded-2xl border border-brand-100 bg-surface px-4 py-3 shadow-lg">
        <span aria-hidden className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-xl bg-brand-50 text-lg">✨</span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold">새 버전이 있습니다</p>
          <p className="mt-0.5 hidden text-xs text-ink-soft sm:block">
            바뀐 기능을 쓰려면 새로고침하세요. 입력 중인 내용이 있으면 저장한 뒤 눌러 주세요.
          </p>
        </div>
        <button type="button" className="btn-primary flex-shrink-0 whitespace-nowrap" onClick={() => window.location.reload()}>
          지금 업데이트
        </button>
        <button type="button" aria-label="나중에" title="나중에" className="-mr-1 -mt-1 flex-shrink-0 self-start px-1 text-ink-faint hover:text-red-600"
                onClick={() => setDismissed(latest)}>
          ✕
        </button>
      </div>
    </div>
  );
}
